// @flow
import React from 'react';
import { injectIntl, defineMessages, type IntlShape } from 'react-intl';
import { connect } from 'react-redux';
import core from '@bravissimolabs/website-core';
import { pick } from 'lodash';

import MarkDown from 'brastrap/editorial/markdown/Markdown';
import Bandeau from 'brastrap/common/bandeau/Bandeau';
import type { Bandeau as BandeauType } from 'brastrap/common/bandeau/bandeau.types';
import Chat from 'brastrap/containers/chat/ChatContainer';
import Container from 'brastrap/common/container/Container';
import SectionNav from 'brastrap/common/section-nav/SectionNav';
import Block from 'brastrap/editorial/block/Block';
import type { Block as BlockType } from 'brastrap/editorial/block/block.types';
import Grid from 'brastrap/helpers/l-grid/l-grid';
import Link from 'brastrap/containers/link/LinkContainer';

import type { LinkType } from '../../HelpSection/containers/HelpSectionPageContainer';
import Meta from '../../../containers/MetaContainer';
import { setHelp } from '../../../actions/help';

type Props = {
  bandeau: BandeauType,
  sectionNavLinks: LinkType[],
  content: FaqContent,
  block: BlockType,
  intl: IntlShape,
};

export const HelpContentPage = (props: Props) => {
  let relatedArticles;
  if (props.content.relatedFaqs) {
    relatedArticles = props.content.relatedFaqs.map((item, index) => (
      <li key={index}>
        <Link href={item.faqLink}>{item.question}</Link>
      </li>
    ));
  }

  const messages = defineMessages({
    pageTitle: {
      id: 'help-landing.title',
      defaultMessage: 'Help & FAQs',
    },
    relatedArticlesLabel: {
      id: 'help-content.related-articles-label',
      defaultMessage: 'Related articles',
    },
  });

  const currentSection = props.sectionNavLinks.find(section => section.current);
  const bandeauProps = {
    ...props.bandeau,
    title: props.intl.formatMessage(messages.pageTitle),
  };
  if (currentSection) {
    bandeauProps.returnNav = pick(currentSection, 'label', 'url');
  }

  return (
    <Meta
      title={props.content.pageTitle || props.content.question}
      description={
        props.content.pageDescription ||
        props.content.answer.slice(0, 157).replace(/\n/g, ' ') +
          (props.content.answer.length > 157 ? '...' : '')
      }
    >
      <Bandeau {...bandeauProps}>
        <Chat />
      </Bandeau>
      <Container>
        <Grid>
          <Grid.Unit modifiers="nav">
            <SectionNav items={props.sectionNavLinks} />
          </Grid.Unit>

          <Grid.Unit modifiers="content">
            <div className="s-prose">
              <h2>{props.content.question}</h2>
              <MarkDown content={props.content.answer} />
            </div>

            <hr className="u-rule u-rule--large" />
            {props.content.relatedFaqs && (
              <Block
                {...props.block}
                title={props.intl.formatMessage(messages.relatedArticlesLabel)}
              >
                <ul>{relatedArticles}</ul>
              </Block>
            )}
          </Grid.Unit>
        </Grid>
      </Container>
    </Meta>
  );
};

HelpContentPage.defaultProps = {
  bandeau: {},
  sectionNavLinks: [],
  block: {
    modifiers: ['offset'],
  },
  content: {},
};

HelpContentPage.populateStore = async (store, props) => {
  const helpApi = core.help.help;
  const topic = props.params.topic;
  const question = props.params.faq;
  const help = await helpApi.getFaqByQuestion(topic, question);
  if (!help.content) {
    throw new Error('404');
  }
  return store.dispatch(setHelp(help));
};

const mapStateToProps = state => ({
  sectionNavLinks: state.help.data.sectionNav,
  content: state.help.data.content,
});

export default connect(mapStateToProps)(injectIntl(HelpContentPage));
